/**
 * RatingsBreakdown Component
 *
 * Displays campaign ratings breakdown by rater type with weighted average.
 * Shows: Admin (40%), Evaluator (30%), Investor (30%) ratings.
 *
 * The weighted average is calculated by the backend and displayed here.
 * Admin panel just displays the breakdown - calculation logic is server-side.
 */

import React from "react";
import {
  Box,
  Typography,
  Rating,
  LinearProgress,
  Skeleton,
  Chip,
  Tooltip,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PeopleIcon from "@mui/icons-material/People";
import "./RatingsBreakdown.scss";

// Weight percentages for each rater type
const WEIGHTS = {
  admin: { label: "Admin", weight: 40, icon: AdminPanelSettingsIcon, color: "#1976d2" },
  evaluator: { label: "Evaluator", weight: 30, icon: AssessmentIcon, color: "#7b1fa2" },
  investor: { label: "Investor", weight: 30, icon: PeopleIcon, color: "#388e3c" },
};

const RatingRow = ({ type, average, count, loading }) => {
  const config = WEIGHTS[type];
  if (!config) return null;

  const Icon = config.icon;

  if (loading) {
    return (
      <Box className="ratings-breakdown__row">
        <Skeleton variant="text" width={100} />
        <Skeleton variant="rectangular" width={120} height={24} />
        <Skeleton variant="text" width={60} />
      </Box>
    );
  }

  // Safely parse average to a number - guard against strings and invalid values
  // Note: Number(null) returns 0, which would incorrectly show a rating when none exists
  const numericAverage = average != null && Number.isFinite(Number(average))
    ? Number(average)
    : null;
  const hasRating = numericAverage !== null && count > 0;

  return (
    <Box className="ratings-breakdown__row">
      <Box className="ratings-breakdown__label">
        <Icon sx={{ color: config.color, fontSize: 20, marginRight: 1 }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {config.label}
        </Typography>
        <Tooltip title={`${config.weight}% of weighted average`}>
          <Chip
            label={`${config.weight}%`}
            size="small"
            sx={{
              marginLeft: 1,
              height: 20,
              fontSize: 10,
              backgroundColor: config.color,
              color: "#fff",
            }}
          />
        </Tooltip>
      </Box>

      <Box className="ratings-breakdown__rating">
        {hasRating ? (
          <>
            <Rating value={numericAverage} precision={0.1} size="small" readOnly />
            <Typography variant="body2" sx={{ marginLeft: 1, color: "#666" }}>
              {numericAverage.toFixed(1)}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" sx={{ color: "#999", fontStyle: "italic" }}>
            No ratings yet
          </Typography>
        )}
      </Box>

      <Typography variant="caption" className="ratings-breakdown__count">
        {count || 0} {count === 1 ? "rating" : "ratings"}
      </Typography>
    </Box>
  );
};

const RatingsBreakdown = ({ ratings, loading = false }) => {
  if (loading) {
    return (
      <Box className="ratings-breakdown ratings-breakdown--loading">
        <Typography variant="subtitle2" className="ratings-breakdown__title">
          Ratings Breakdown
        </Typography>
        <RatingRow type="admin" loading />
        <RatingRow type="evaluator" loading />
        <RatingRow type="investor" loading />
      </Box>
    );
  }

  if (!ratings) {
    return (
      <Box className="ratings-breakdown ratings-breakdown--empty">
        <Typography variant="subtitle2" className="ratings-breakdown__title">
          Ratings Breakdown
        </Typography>
        <Typography variant="body2" sx={{ color: "#999", fontStyle: "italic" }}>
          No rating data available
        </Typography>
      </Box>
    );
  }

  // Normalize field names from API response
  const breakdown = ratings.breakdown || {};
  const adminData = breakdown.admin || {};
  const evaluatorData = breakdown.evaluator || {};
  const investorData = breakdown.investor || {};

  const rawWeightedAverage =
    ratings.weighted_average ?? ratings.weightedAverage ?? ratings.average_rating ?? null;
  const totalCount = ratings.rating_count ?? ratings.ratingCount ?? 0;

  // Safely parse weighted average - guard against strings and invalid values
  const weightedAverage = rawWeightedAverage != null && Number.isFinite(Number(rawWeightedAverage))
    ? Number(rawWeightedAverage)
    : null;

  return (
    <Box className="ratings-breakdown">
      <Typography variant="subtitle2" className="ratings-breakdown__title">
        Ratings Breakdown
      </Typography>

      {/* Weighted Average - Highlighted */}
      {weightedAverage !== null && (
        <Box className="ratings-breakdown__weighted">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Weighted Average
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Rating value={weightedAverage} precision={0.1} size="medium" readOnly />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1976d2" }}>
              {weightedAverage.toFixed(1)}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: "#666" }}>
            Based on {totalCount} total {totalCount === 1 ? "rating" : "ratings"}
          </Typography>
        </Box>
      )}

      {/* Individual Breakdowns */}
      <Box className="ratings-breakdown__rows">
        <RatingRow
          type="admin"
          average={adminData.average}
          count={adminData.count}
        />
        <RatingRow
          type="evaluator"
          average={evaluatorData.average}
          count={evaluatorData.count}
        />
        <RatingRow
          type="investor"
          average={investorData.average}
          count={investorData.count}
        />
      </Box>

      {/* Weight Explanation */}
      <Typography variant="caption" className="ratings-breakdown__formula">
        Formula: (Admin × 40%) + (Evaluator × 30%) + (Investor × 30%)
      </Typography>
    </Box>
  );
};

export default RatingsBreakdown;
